import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, FormControlLabel, Grid, Paper, Switch, Typography } from '@mui/material';

import type React from 'react';

import { PERMISSIONS, type Permission } from '../../shared/constants/permissions';
import { getPermissionsForRole } from '../../shared/constants/roles';
import type { UserRole } from '../../types/auth';
import { PermissionTooltip } from '../common/PermissionTooltip';

const PERMISSION_DESCRIPTIONS: Record<string, string> = {
  [PERMISSIONS.TENDER_CREATE]: 'Create new tenders',
  [PERMISSIONS.TENDER_VIEW]: 'View assigned tenders',
  [PERMISSIONS.TENDER_VIEW_CLIENT]: 'View all tenders in organization',
  [PERMISSIONS.TENDER_FILES_VIEW_CLIENT]: 'View all files in tenders',
  [PERMISSIONS.TENDER_SECTIONS_VIEW_CLIENT]: 'View all sections in tenders',
  [PERMISSIONS.TENDER_EDIT]: 'Edit assigned tenders',
  [PERMISSIONS.TENDER_FULL_ACCESS]: 'Full access to assigned tenders',
  [PERMISSIONS.TENDER_DELETE]: 'Delete tenders',
  [PERMISSIONS.TENDER_MANAGE_TEAM]: 'Manage tender teams',
  [PERMISSIONS.DRAFT_CREATE]: 'Create drafts',
  [PERMISSIONS.DRAFT_EDIT]: 'Edit drafts',
  [PERMISSIONS.REVIEW_CREATE]: 'Create reviews',
  [PERMISSIONS.REPORT_CREATE]: 'Generate capability reports',
  [PERMISSIONS.INSIGHTS_VIEW]: 'View strategic insights dashboard',
  [PERMISSIONS.USER_INVITE]: 'Invite users',
  [PERMISSIONS.USER_VIEW]: 'View users',
  [PERMISSIONS.USER_UPDATE]: 'Update users',
  [PERMISSIONS.USER_DELETE]: 'Delete users',
  [PERMISSIONS.USER_MANAGE_ROLES]: 'Manage user roles',
  [PERMISSIONS.USER_MANAGE_LIMITS]: 'Manage user limits',
  [PERMISSIONS.TEAM_CREATE]: 'Create teams',
  [PERMISSIONS.TEAM_VIEW]: 'View teams',
  [PERMISSIONS.TEAM_UPDATE]: 'Update teams',
  [PERMISSIONS.TEAM_DELETE]: 'Delete teams',
  [PERMISSIONS.TEAM_MANAGE_LIMITS]: 'Manage team limits',
  [PERMISSIONS.CLIENT_PROFILE_UPDATE]: 'Update company profile',
  [PERMISSIONS.CLIENT_VIEW]: 'View company profile',
  [PERMISSIONS.BILLING_MANAGE]: 'Manage billing',
  [PERMISSIONS.SETTINGS_MANAGE]: 'Manage system settings',
  [PERMISSIONS.TICKET_CREATE]: 'Create support tickets',
  [PERMISSIONS.TICKET_VIEW]: 'View support tickets',
  [PERMISSIONS.TICKET_UPDATE]: 'Update support tickets',
  [PERMISSIONS.TICKET_MANAGE]: 'Manage support tickets',
  [PERMISSIONS.TEMPLATE_VIEW_CLIENT]: 'View templates',
  [PERMISSIONS.TEMPLATE_CREATE]: 'Create templates',
  [PERMISSIONS.TEMPLATE_EDIT]: 'Edit templates',
  [PERMISSIONS.TEMPLATE_DELETE]: 'Delete templates',
  [PERMISSIONS.TEMPLATE_UPLOAD_TEAM]: 'Upload Team templates',
  [PERMISSIONS.TEMPLATE_MANAGE_CLIENT]: 'Manage Company templates',
  [PERMISSIONS.LIBRARY_VIEW_CLIENT]: 'View document library',
  [PERMISSIONS.LIBRARY_UPLOAD_TEAM]: 'Upload to Team document library',
  [PERMISSIONS.LIBRARY_DELETE]: 'Delete from Team document library',
  [PERMISSIONS.LIBRARY_MANAGE_CLIENT]: 'Manage Company document library (incl. hidden files)',
  [PERMISSIONS.PUBLIC_TENDER_VIEW]: 'View public tenders',
  [PERMISSIONS.PARTNER_VIEW]: 'View partner profiles',
  [PERMISSIONS.PARTNER_MANAGE]: 'Manage partner relationships',
  [PERMISSIONS.PROCUREMENT_ACCESS]: 'Access the Procurement Ecosystem',
};

const PERMISSION_GROUPS = [
  {
    title: 'User Controls',
    permissions: [
      PERMISSIONS.TENDER_VIEW,
      PERMISSIONS.TENDER_CREATE,
      PERMISSIONS.TENDER_EDIT,
      PERMISSIONS.PUBLIC_TENDER_VIEW,
      PERMISSIONS.DRAFT_CREATE,
      PERMISSIONS.DRAFT_EDIT,
      PERMISSIONS.REVIEW_CREATE,
      PERMISSIONS.REPORT_CREATE,
      PERMISSIONS.LIBRARY_VIEW_CLIENT,
      PERMISSIONS.TEMPLATE_VIEW_CLIENT,
      PERMISSIONS.TEMPLATE_CREATE,
      PERMISSIONS.TEMPLATE_EDIT,
      PERMISSIONS.TEMPLATE_DELETE,
      PERMISSIONS.TICKET_VIEW,
      PERMISSIONS.TICKET_CREATE,
      PERMISSIONS.TICKET_UPDATE,
    ],
  },
  {
    title: 'Team Controls',
    permissions: [
      PERMISSIONS.TEAM_VIEW,
      PERMISSIONS.LIBRARY_UPLOAD_TEAM,
      PERMISSIONS.LIBRARY_DELETE,
      PERMISSIONS.TEMPLATE_UPLOAD_TEAM,
      PERMISSIONS.TENDER_MANAGE_TEAM,
      PERMISSIONS.TENDER_SECTIONS_VIEW_CLIENT,
      PERMISSIONS.TENDER_FILES_VIEW_CLIENT,
    ],
  },
  {
    title: 'Client & Admin Controls',
    permissions: [
      PERMISSIONS.TENDER_VIEW_CLIENT,
      PERMISSIONS.TENDER_DELETE,
      PERMISSIONS.INSIGHTS_VIEW,
      PERMISSIONS.PARTNER_VIEW,
      PERMISSIONS.PARTNER_MANAGE,
      PERMISSIONS.USER_VIEW,
      PERMISSIONS.USER_INVITE,
      PERMISSIONS.USER_UPDATE,
      PERMISSIONS.USER_DELETE,
      PERMISSIONS.USER_MANAGE_ROLES,
      PERMISSIONS.USER_MANAGE_LIMITS,
      PERMISSIONS.TEAM_CREATE,
      PERMISSIONS.TEAM_UPDATE,
      PERMISSIONS.TEAM_DELETE,
      PERMISSIONS.TEAM_MANAGE_LIMITS,
      PERMISSIONS.LIBRARY_MANAGE_CLIENT,
      PERMISSIONS.TEMPLATE_MANAGE_CLIENT,
      PERMISSIONS.CLIENT_VIEW,
      PERMISSIONS.CLIENT_PROFILE_UPDATE,
      PERMISSIONS.BILLING_MANAGE,
      PERMISSIONS.SETTINGS_MANAGE,
      PERMISSIONS.TICKET_MANAGE,
    ],
  },
  {
    title: 'Procurement Controls',
    permissions: [PERMISSIONS.PROCUREMENT_ACCESS],
  },
];

interface UserPermissionsFormProps {
  role: UserRole;
  permissions: string[];
  isCustomPermissions: boolean;
  canEditUser: boolean;
  canManageRoles: boolean;
  onCustomSwitchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPermissionToggle: (perm: string) => void;
}

export const UserPermissionsForm: React.FC<UserPermissionsFormProps> = ({
  role,
  permissions,
  isCustomPermissions,
  canManageRoles,
  onCustomSwitchChange,
  onPermissionToggle,
}) => {
  const effectivePermissions = isCustomPermissions ? permissions : getPermissionsForRole(role);

  const visiblePermissions = effectivePermissions;

  const allPermissions = Object.values(PERMISSIONS);

  const permissionsToShow = isCustomPermissions ? allPermissions : visiblePermissions;
  const renderedSet = new Set<string>();

  return (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6">Permissions</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={isCustomPermissions}
              onChange={onCustomSwitchChange}
              size="small"
              disabled={!canManageRoles}
            />
          }
          label="Customize"
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {(() => {
          return PERMISSION_GROUPS.map((group) => {
            const groupPerms = group.permissions.filter((p) => permissionsToShow.includes(p));
            if (groupPerms.length === 0) {
              return null;
            }
            for (const p of groupPerms) {
              renderedSet.add(p);
            }

            return (
              <Box key={group.title}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', fontSize: '0.75rem', mb: 1 }}
                >
                  {group.title}
                </Typography>
                <Grid container spacing={1}>
                  {groupPerms.map((perm) => (
                    <Grid size={{ xs: 12 }} key={perm}>
                      {isCustomPermissions ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={permissions.includes(perm)}
                                onChange={() => onPermissionToggle(perm)}
                                size="small"
                                disabled={!canManageRoles}
                              />
                            }
                            label={PERMISSION_DESCRIPTIONS[perm] || perm}
                          />
                          <PermissionTooltip
                            permissionKey={perm as Permission}
                            isGranted={permissions.includes(perm)}
                          />
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon color="success" sx={{ fontSize: 16, opacity: 0.7 }} />
                          <Typography variant="body2">
                            {PERMISSION_DESCRIPTIONS[perm] || perm}
                          </Typography>
                          <PermissionTooltip permissionKey={perm as Permission} isGranted={true} />
                        </Box>
                      )}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          });
        })()}
      </Box>
    </Paper>
  );
};
